import { useState } from 'react';
import './ApplyModal.css';

export default function ApplyModal({ isOpen, onClose, jobImage, jobIndex }) {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        linkedinUrl: '',
        githubUrl: '',
        portfolioUrl: '',
        workType: '',
        expectedSalary: '',
        coverLetter: ''
    });
    const [resumeFile, setResumeFile] = useState(null);
    const [status, setStatus] = useState({ loading: false, success: false, error: null });
    const [dragActive, setDragActive] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);

    if (!isOpen) return null;

    // Derive a clean job title or role name from filename if possible
    const formattedJobTitle = jobImage
        ? jobImage.replace(/[-_]/g, ' ').replace(/\.[^/.]+$/, '').replace(/\b\w/g, l => l.toUpperCase())
        : `Open Position #${(jobIndex !== null && jobIndex !== undefined) ? jobIndex + 1 : ''}`;

    // Detect if this position is a contract role (e.g., contains 'contract' in filename or title)
    const isContractPosition = /contract/i.test(jobImage || '') || /contract/i.test(formattedJobTitle);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateAndSetFile = (file) => {
        const allowedExtensions = ['pdf', 'doc', 'docx', 'rtf'];
        const extension = file.name.split('.').pop().toLowerCase();

        if (!allowedExtensions.includes(extension)) {
            setStatus({ loading: false, success: false, error: 'Only PDF, DOC, or DOCX files are allowed.' });
            return;
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB
            setStatus({ loading: false, success: false, error: 'File size must be 10MB or less.' });
            return;
        }

        setResumeFile(file);
        setStatus(prev => ({ ...prev, error: null }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            validateAndSetFile(e.target.files[0]);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    // Derive a slug for direct sharing: e.g. "backend-engineer-intern"
    const jobSlug = jobImage
        ? jobImage.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        : `job-${(jobIndex !== null && jobIndex !== undefined) ? jobIndex + 1 : '1'}`;

    const handleCopyShareLink = async (e) => {
        e.preventDefault();
        const shareUrl = `${window.location.origin}/careers/${jobSlug}`;

        // 1. Mobile Native Social Share Sheet
        if (navigator.share) {
            try {
                await navigator.share({
                    url: shareUrl
                });
                return;
            } catch (err) {
                if (err.name === 'AbortError') return;
            }
        }

        // 2. Fallback to copying to clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 2500);
            }).catch(() => { });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.fullName.trim() || !formData.email.trim()) {
            setStatus({ loading: false, success: false, error: 'Please fill in your name and email.' });
            return;
        }

        if (isContractPosition) {
            if (!formData.workType) {
                setStatus({ loading: false, success: false, error: 'Please select a Work Type (Full-time or Part-time).' });
                return;
            }
            if (!formData.expectedSalary.trim()) {
                setStatus({ loading: false, success: false, error: 'Please specify your Expected Salary.' });
                return;
            }
        }

        if (!resumeFile) {
            setStatus({ loading: false, success: false, error: 'Please upload your CV / Resume.' });
            return;
        }

        setStatus({ loading: true, success: false, error: null });

        try {
            const data = new FormData();
            data.append('fullName', formData.fullName.trim());
            data.append('email', formData.email.trim());
            data.append('phone', formData.phone.trim());
            data.append('linkedinUrl', formData.linkedinUrl.trim());
            data.append('githubUrl', formData.githubUrl.trim());
            data.append('portfolioUrl', formData.portfolioUrl.trim());
            if (formData.workType) {
                data.append('workType', formData.workType);
                data.append('joinType', formData.workType); // backwards compatibility
            }
            if (formData.expectedSalary.trim()) {
                const rawSalary = formData.expectedSalary.trim();
                const formattedSalary = /^(rs\.?|lkr)/i.test(rawSalary) ? rawSalary : `Rs. ${rawSalary}`;
                data.append('expectedSalary', formattedSalary);
            }
            data.append('isContractPosition', isContractPosition ? 'true' : 'false');
            data.append('coverLetter', formData.coverLetter.trim());
            data.append('jobTitle', formattedJobTitle);
            if (jobImage) data.append('jobImage', jobImage);
            data.append('resume', resumeFile);

            // API endpoint:
            // In local development, defaults to http://localhost:5001/api/careers/apply
            // On production shared hosting, falls back to the native /api/careers/apply.php
            const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            let endpoint = '/api/careers/apply.php';
            if (process.env.REACT_APP_API_URL) {
                endpoint = `${process.env.REACT_APP_API_URL}/api/careers/apply`;
            } else if (isLocalDev) {
                endpoint = 'http://localhost:5001/api/careers/apply';
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                body: data
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to submit application. Please check mail settings.');
            }

            setStatus({ loading: false, success: true, error: null });
        } catch (err) {
            setStatus({
                loading: false,
                success: false,
                error: err.message || 'Something went wrong while submitting your application.'
            });
        }
    };

    const resetForm = () => {
        setFormData({
            fullName: '',
            email: '',
            phone: '',
            linkedinUrl: '',
            githubUrl: '',
            portfolioUrl: '',
            workType: '',
            expectedSalary: '',
            coverLetter: ''
        });
        setResumeFile(null);
        setStatus({ loading: false, success: false, error: null });
        onClose();
    };

    return (
        <div className="apply-modal-backdrop" onClick={onClose}>
            <div className="apply-modal-card" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="apply-modal-close" onClick={onClose} aria-label="Close">
                    <i className="fas fa-times"></i>
                </button>

                {status.success ? (
                    <div className="apply-success-view">
                        <div className="success-icon-wrap">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <h3>Application Submitted!</h3>
                        <p className="success-desc">
                            Thank you, <strong>{formData.fullName}</strong>. We've forwarded your application and resume to our recruitment team via our Outlook mail server. A confirmation copy has also been sent to <strong>{formData.email}</strong>.
                        </p>
                        <div className="applied-job-badge">
                            <i className="fas fa-briefcase mr-2"></i>
                            Position: {formattedJobTitle}
                        </div>
                        {formData.workType && (
                            <div className="applied-contract-badge mt-2">
                                <i className="fas fa-file-contract mr-2"></i>
                                Contract &bull; {formData.workType} &bull; Expected Salary: {/^(rs\.?|lkr)/i.test(formData.expectedSalary.trim()) ? formData.expectedSalary.trim() : `Rs. ${formData.expectedSalary.trim()}`} / month
                            </div>
                        )}
                        <button type="button" className="theme-btn mt-30" onClick={resetForm}>
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="apply-modal-header">
                            <div className="apply-modal-header-top">
                                <span className="apply-pill-tag">
                                    <i className="fas fa-paper-plane mr-2"></i> Direct Career Application
                                </span>
                                <button
                                    type="button"
                                    className={`share-job-btn ${copiedLink ? 'copied' : ''}`}
                                    onClick={handleCopyShareLink}
                                    title="Copy direct link to this job application"
                                >
                                    <i className={`fas ${copiedLink ? 'fa-check' : 'fa-share-alt'} mr-2`}></i>
                                    {copiedLink ? 'Link Copied!' : 'Share Job Link'}
                                </button>
                            </div>
                            <h2>Apply for this Position</h2>
                            <p className="apply-job-subtext">
                                Position: <strong>{formattedJobTitle}</strong>
                            </p>
                        </div>

                        {status.error && (
                            <div className="apply-error-banner">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                {status.error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="apply-form-body">
                            <div className="form-row-2">
                                <div className="apply-input-group">
                                    <label htmlFor="fullName">Full Name <span className="req">*</span></label>
                                    <div className="input-wrap">
                                        <i className="far fa-user"></i>
                                        <input
                                            type="text"
                                            id="fullName"
                                            name="fullName"
                                            placeholder="e.g. John Doe"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="apply-input-group">
                                    <label htmlFor="email">Email Address <span className="req">*</span></label>
                                    <div className="input-wrap">
                                        <i className="far fa-envelope"></i>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="john.doe@example.com"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-row-2">
                                <div className="apply-input-group">
                                    <label htmlFor="phone">Phone Number</label>
                                    <div className="input-wrap">
                                        <i className="fas fa-phone-alt"></i>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            placeholder="+94 00 0000000"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="apply-input-group">
                                    <label htmlFor="linkedinUrl">LinkedIn Profile URL</label>
                                    <div className="input-wrap">
                                        <i className="fab fa-linkedin-in"></i>
                                        <input
                                            type="url"
                                            id="linkedinUrl"
                                            name="linkedinUrl"
                                            placeholder="https://linkedin.com/in/username"
                                            value={formData.linkedinUrl}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-row-2">
                                <div className="apply-input-group">
                                    <label htmlFor="githubUrl">GitHub Profile URL</label>
                                    <div className="input-wrap">
                                        <i className="fab fa-github"></i>
                                        <input
                                            type="url"
                                            id="githubUrl"
                                            name="githubUrl"
                                            placeholder="https://github.com/username"
                                            value={formData.githubUrl}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="apply-input-group">
                                    <label htmlFor="portfolioUrl">Portfolio / Personal Website</label>
                                    <div className="input-wrap">
                                        <i className="fas fa-globe"></i>
                                        <input
                                            type="url"
                                            id="portfolioUrl"
                                            name="portfolioUrl"
                                            placeholder="https://yourportfolio.com"
                                            value={formData.portfolioUrl}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contract Position Specific Fields */}
                            {isContractPosition && (
                                <div className="contract-fields-section">
                                    <div className="contract-badge-header">
                                        <i className="fas fa-file-contract mr-2"></i> Contract Position Details
                                    </div>
                                    <div className="form-row-2">
                                        <div className="apply-input-group">
                                            <label htmlFor="workType">
                                                Work Type <span className="req">*</span>
                                            </label>
                                            <div className="input-wrap select-input-wrap">
                                                <i className="fas fa-briefcase"></i>
                                                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                                                <select
                                                    id="workType"
                                                    name="workType"
                                                    className="apply-select-input"
                                                    value={formData.workType}
                                                    onChange={(e) => {
                                                        const newWorkType = e.target.value;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            workType: newWorkType,
                                                            // Clear expectedSalary when switching work type so selection stays valid
                                                            expectedSalary: ''
                                                        }));
                                                    }}
                                                    onBlur={handleInputChange}
                                                    required
                                                >
                                                    <option value="" disabled>Choose...</option>
                                                    <option value="Full-time">Full-time</option>
                                                    <option value="Part-time">Part-time</option>
                                                </select>
                                                <i className="fas fa-chevron-down select-chevron"></i>
                                            </div>
                                        </div>

                                        <div className="apply-input-group">
                                            <label htmlFor="expectedSalary">
                                                Expected Salary (LKR / Month) <span className="req">*</span>
                                            </label>
                                            <div className="input-wrap select-input-wrap">
                                                <span className="currency-prefix-tag">Rs.</span>
                                                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                                                <select
                                                    id="expectedSalary"
                                                    name="expectedSalary"
                                                    className="apply-select-input has-currency-prefix"
                                                    value={formData.expectedSalary}
                                                    onChange={handleInputChange}
                                                    onBlur={handleInputChange}
                                                    required
                                                >
                                                    <option value="" disabled>Choose...</option>
                                                    {formData.workType === 'Part-time' && (
                                                        <>
                                                            <option value="30,000">30,000</option>
                                                            <option value="40,000">40,000</option>
                                                            <option value="50,000">50,000</option>
                                                            <option value="60,000">60,000</option>
                                                            <option value="70,000">70,000</option>
                                                            <option value="80,000">80,000</option>
                                                        </>
                                                    )}
                                                    {formData.workType === 'Full-time' && (
                                                        <>
                                                            <option value="80,000">80,000</option>
                                                            <option value="90,000">90,000</option>
                                                            <option value="100,000">100,000</option>
                                                            <option value="110,000">110,000</option>
                                                            <option value="120,000">120,000</option>
                                                            <option value="130,000">130,000</option>
                                                            <option value="140,000">140,000</option>
                                                            <option value="150,000">150,000</option>
                                                        </>
                                                    )}
                                                    {!formData.workType && (
                                                        <>
                                                            <option value="30,000">30,000</option>
                                                            <option value="40,000">40,000</option>
                                                            <option value="50,000">50,000</option>
                                                            <option value="60,000">60,000</option>
                                                            <option value="70,000">70,000</option>
                                                            <option value="80,000">80,000</option>
                                                            <option value="90,000">90,000</option>
                                                            <option value="100,000">100,000</option>
                                                            <option value="110,000">110,000</option>
                                                            <option value="120,000">120,000</option>
                                                            <option value="130,000">130,000</option>
                                                            <option value="140,000">140,000</option>
                                                            <option value="150,000">150,000</option>
                                                        </>
                                                    )}
                                                </select>
                                                <i className="fas fa-chevron-down select-chevron"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Resume Upload Drag & Drop */}
                            <div className="apply-input-group">
                                <label>Resume / CV (PDF, DOC, DOCX) <span className="req">*</span></label>
                                <div
                                    className={`file-upload-dropzone ${dragActive ? 'active' : ''} ${resumeFile ? 'has-file' : ''}`}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        type="file"
                                        id="resumeUpload"
                                        className="file-hidden-input"
                                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor="resumeUpload" className="file-drop-label">
                                        {resumeFile ? (
                                            <div className="file-selected-info">
                                                <i className="fas fa-file-pdf file-icon"></i>
                                                <div className="file-meta">
                                                    <span className="file-name">{resumeFile.name}</span>
                                                    <span className="file-size">({(resumeFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                                                </div>
                                                <span className="change-file-btn">Change</span>
                                            </div>
                                        ) : (
                                            <div className="file-prompt">
                                                <i className="fas fa-cloud-upload-alt upload-icon"></i>
                                                <span><strong>Click to upload</strong> or drag and drop your Resume/CV</span>
                                                <small>PDF, DOC, DOCX up to 10MB</small>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>

                            <div className="apply-input-group">
                                <label htmlFor="coverLetter">Cover Letter / Note to Hiring Manager</label>
                                <textarea
                                    id="coverLetter"
                                    name="coverLetter"
                                    rows="3"
                                    placeholder="Briefly introduce yourself, relevant experience, or key achievements..."
                                    value={formData.coverLetter}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>

                            <div className="apply-form-footer">
                                <button type="button" className="btn-cancel" onClick={onClose} disabled={status.loading}>
                                    Cancel
                                </button>
                                <button type="submit" className="theme-btn btn-submit" disabled={status.loading}>
                                    {status.loading ? (
                                        <>
                                            <i className="fas fa-circle-notch fa-spin mr-2"></i> Sending Application...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane mr-2"></i> Submit Application
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
